import { Table, Button, Tag, Space, Popconfirm, App } from 'antd'
import { BarChartOutlined, EditOutlined, DeleteOutlined, UnorderedListOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { deleteSurvey } from '../services/survey-config-api'
import type { SurveyConfig } from '../types/survey.types'
import type { ColumnsType } from 'antd/es/table'
import './SurveyConfigTable.css'

interface SurveyConfigTableProps {
  surveys: SurveyConfig[]
  loading: boolean
  onEdit: (survey: SurveyConfig) => void
  onManageQuestions: (survey: SurveyConfig) => void
  onViewResponses: (survey: SurveyConfig) => void
  onRefresh: () => void
}

export function SurveyConfigTable({
  surveys,
  loading,
  onEdit,
  onManageQuestions,
  onViewResponses,
  onRefresh
}: SurveyConfigTableProps) {
  const { message } = App.useApp()

  const handleDelete = async (id: string) => {
    try {
      await deleteSurvey(id)
      message.success('Survey deleted')
      onRefresh()
    } catch (error: any) {
      console.error('Error deleting survey:', error)
      message.error(error.response?.data?.message || 'Failed to delete survey')
    }
  }

  const getStatus = (survey: SurveyConfig) => {
    if (!survey.isActive) {
      return { label: 'Inactive', color: 'default' }
    }
    const now = dayjs()
    const start = dayjs(survey.startDate)
    const end = dayjs(survey.endDate)

    if (now.isBefore(start)) {
      return { label: 'Scheduled', color: 'blue' }
    }
    if (now.isAfter(end)) {
      return { label: 'Expired', color: 'orange' }
    }
    return { label: 'Active', color: 'green' }
  }

  const parseTargetTenants = (targetTenantCodes: string | null): string[] | null => {
    if (!targetTenantCodes) return null
    try {
      return JSON.parse(targetTenantCodes)
    } catch {
      return null
    }
  }

  const columns: ColumnsType<SurveyConfig> = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      sorter: (a, b) => a.title.localeCompare(b.title)
    },
    {
      title: 'Target',
      key: 'target',
      width: 200,
      render: (_, record) => {
        const tenants = parseTargetTenants(record.targetTenantCodes)
        if (!tenants) {
          return (
            <Tag color="purple" style={{ fontSize: '11px', padding: '0 7px' }}>
              All tenants
            </Tag>
          )
        }
        return (
          <Space size={2} wrap>
            {tenants.map(code => (
              <Tag key={code} style={{ fontSize: '11px', padding: '0 7px' }}>
                {code}
              </Tag>
            ))}
          </Space>
        )
      }
    },
    {
      title: 'Start',
      dataIndex: 'startDate',
      key: 'startDate',
      width: 120,
      sorter: (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm')
    },
    {
      title: 'End',
      dataIndex: 'endDate',
      key: 'endDate',
      width: 120,
      sorter: (a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime(),
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm')
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      sorter: (a, b) => getStatus(a).label.localeCompare(getStatus(b).label),
      render: (_, record) => {
        const status = getStatus(record)
        return (
          <Tag color={status.color} style={{ fontSize: '11px', padding: '0 7px' }}>
            {status.label}
          </Tag>
        )
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<UnorderedListOutlined />}
            onClick={() => onManageQuestions(record)}
            title="Questions"
          />
          <Button
            type="text"
            size="small"
            icon={<BarChartOutlined />}
            onClick={() => onViewResponses(record)}
            title="Responses"
          />
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => onEdit(record)} />
          <Popconfirm
            title="Delete survey"
            description="Are you sure you want to delete this survey?"
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div className="survey-config-table-wrapper" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Table
        size="small"
        columns={columns}
        dataSource={surveys}
        rowKey="id"
        loading={loading}
        scroll={{ x: 'max-content', y: 'calc(100vh - 200px)' }}
        pagination={false}
        style={{ flex: 1 }}
      />
    </div>
  )
}
